import type { HelpGuide } from '../types';

export const opcontractGuide: HelpGuide = {
  key: 'opcontract',
  title: 'สัญญาดำเนินงาน (Operating Service Contracts)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ สัญญาดำเนินงาน',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/opcontract/คู่มือการใช้งาน_สัญญาดำเนินงาน_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “สัญญาดำเนินงาน (Operating Service Contracts)” ใช้คุมสัญญาบริการรายเดือนที่ “ไม่เข้า TFRS 16” เช่น รปภ., ทำความสะอาด, IT Support, ค่าเช่าเครื่องถ่ายเอกสาร, น้ำดื่ม, กำจัดปลวก ฯลฯ ซึ่งไม่ต้องตั้งสินทรัพย์สิทธิการใช้ (ROU) แต่ทยอย “ลงเป็นค่าใช้จ่ายตรงๆ” ทุกเดือนแทน ระบบจะช่วยขึ้นทะเบียนสัญญา สร้างตารางรับรู้ (Recognition Schedule) และลงบัญชีค่าใช้จ่ายรายเดือนผ่านสายอนุมัติ (maker-checker)',
        },
        {
          kind: 'callout',
          text: 'หลักการ — ทำไมไม่เข้า TFRS 16: เข้าข้อยกเว้น 3 แบบ — S = Service Contract (สัญญาบริการ เช่น รปภ./ทำความสะอาด/IT) · L = Low-value Asset (สินทรัพย์มูลค่าต่ำ < 5,000 USD) · T = Short-term (สัญญาสั้น ≤ 12 เดือน) จึงบันทึก: Dr ค่าใช้จ่าย (6xxx) / Cr ค่าใช้จ่ายค้างจ่าย (20170001) หรือ เงินสด (10110002)',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ทะเบียนสัญญา — ', text: 'รายการสัญญาทั้งหมด + ตัวกรอง + KPI (แท็บเริ่มต้น)' },
            {
              lead: 'Recognition Schedule (Matrix) — ',
              text: 'ตารางสัญญา × 12 เดือน แสดงงวดที่ Posted / กลับรายการ / งวดปัจจุบัน',
            },
            {
              lead: 'ประวัติการรับรู้รายเดือน — ',
              text: 'งวดที่รออนุมัติ + ประวัติงวดที่ลงบัญชีแล้ว + ปุ่มกลับรายการ',
            },
            {
              lead: 'สัญญาใกล้หมดอายุ / Renewal — ',
              text: 'KPI 3 ใบ + สัญญาที่ใกล้หมด พร้อมปุ่มต่อสัญญา',
            },
            {
              lead: 'เปิดเผย TFRS 16 — ',
              text: 'หมายเหตุ para 53(c)/(d) + ตารางแยกตามเหตุยกเว้น',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/opcontract/01-register.png',
          caption:
            'ทะเบียนสัญญา — KPI 4 ใบ, การ์ดหลักการ TFRS 16 (ทำไมไม่เข้า: S/L/T) และตารางสัญญาดำเนินงาน',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'มูลค่าสัญญารวม (Active) — ', text: 'มูลค่าสัญญาที่ใช้งานอยู่ ตลอดอายุสัญญา' },
            { lead: 'ค่าใช้จ่ายต่อเดือน — ', text: 'ยอดค่าใช้จ่ายที่ต้องรับรู้รวมต่อเดือน' },
            { lead: 'ใกล้หมดอายุ ≤90 วัน — ', text: 'จำนวนสัญญาที่จะหมดภายใน 90 วัน + ยอด/เดือน' },
            { lead: 'สัญญาใหม่รอเริ่ม — ', text: 'สัญญาที่ลงทะเบียนแล้วแต่ยังไม่ถึงวันเริ่ม' },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีการ์ด “หลักการ — ทำไมไม่เข้า TFRS 16” อธิบายข้อยกเว้น S/L/T ปุ่มมุมขวาบน: เลือกปี · รับรู้รายเดือน · นำเข้า · ส่งออก · เพิ่มสัญญาใหม่ (ปุ่มสีแดง) ตารางทะเบียนกรองด้วย ค้นหา เลขที่/Vendor/รายละเอียด · ประเภท · สถานะ · คู่สัญญา คอลัมน์: เลขที่ · Vendor/รายละเอียด · ประเภท · สาขา · เริ่ม→สิ้นสุด · งวด · ค่าใช้จ่าย/เดือน · มูลค่าสัญญา · ความคืบหน้า · เหลือ (วัน) · สถานะ · การกระทำ (ปุ่ม ดู หรือ ต่อสัญญา → ตามสถานะ)',
        },
        {
          kind: 'callout',
          text: 'หมายเหตุ: ปุ่ม นำเข้า/ส่งออก ทำงานกับไฟล์ Excel (XLSX) เท่านั้น — หน้านี้ไม่มีการแนบไฟล์เอกสารและไม่มีการส่งออกเป็น PDF',
          variant: 'info',
        },
      ],
    },
    {
      id: 'add',
      heading: '3. การเพิ่มสัญญาดำเนินงานใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม เพิ่มสัญญาใหม่ เปิดโมดัล “เพิ่มสัญญาดำเนินงานใหม่” แล้วเลือก ประเภทสัญญา จากการ์ด 10 ใบ (เครื่องถ่ายเอกสาร/รปภ./ทำความสะอาด/น้ำดื่ม/กำจัดปลวก/ดูแลสวน/IT Support/ลิฟต์/แอร์/อินเทอร์เน็ต-โทรศัพท์) จากนั้นกรอก บัญชีค่าใช้จ่าย (GL Code) · คู่สัญญา / Vendor · สาขา · หน่วยงาน (Cost Center) · เหตุที่ไม่เข้า TFRS 16 (Exemption basis) · รายละเอียดบริการ',
        },
        {
          kind: 'para',
          text: 'ส่วน “การคำนวณ Recognition” ระบุ วันเริ่ม · จำนวนงวด (6/12/24/36) · ค่าใช้จ่าย/เดือน แล้วระบบคำนวณ มูลค่าสัญญารวม และกล่อง “จะเกิดอะไรเมื่อบันทึก” ให้อัตโนมัติ พร้อมตั้ง แจ้งล่วงหน้าก่อนหมดอายุ (วัน) และ ต่อสัญญาอัตโนมัติ ได้ กดปุ่ม บันทึก & สร้าง Schedule เพื่อขึ้นทะเบียนและสร้างตารางรับรู้',
        },
        {
          kind: 'callout',
          text: 'หากเลือกเหตุยกเว้นเป็น Short-term (สัญญาสั้น) แต่ระบุจำนวนงวดเกิน 12 เดือน ระบบจะขึ้นเตือนสีแดงและตั้งสถานะ “⚠ ทบทวน TFRS16” เพื่อให้กลับไปตรวจสอบว่าจริงๆ แล้วเข้า TFRS 16 หรือไม่',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/opcontract/02-new-contract.png',
          caption:
            'เพิ่มสัญญาดำเนินงานใหม่ — การ์ดเลือกประเภท 10 ใบ, GL/Vendor/สาขา, ส่วน Recognition และกล่อง “จะเกิดอะไรเมื่อบันทึก”',
        },
      ],
    },
    {
      id: 'schedule',
      heading: '4. Recognition Schedule (Matrix)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Recognition Schedule (Matrix) แสดงตารางสัญญา × 12 เดือน (ม.ค.–ธ.ค.) ของปีที่เลือก ช่วยให้เห็นภาพรวมว่าแต่ละสัญญาในแต่ละเดือนถูกรับรู้ไปแล้วหรือยัง คอลัมน์: สัญญา · Vendor · ม.ค.…ธ.ค. · รวมปี และแถวสรุป รวมทุกสัญญา',
        },
        {
          kind: 'para',
          text: 'สีของช่อง (ดูคำอธิบาย legend ด้านล่าง): Posted = เขียว (ลงบัญชีแล้ว) · กลับรายการ = แดงขีดฆ่า · งวดปัจจุบัน = สีแบรนด์ · รอประมวลผล = เทา',
        },
        {
          kind: 'image',
          src: '/help/opcontract/03-schedule-matrix.png',
          caption:
            'Recognition Schedule (Matrix) — ตารางสัญญา × 12 เดือน พร้อมสถานะ Posted / กลับรายการ / งวดปัจจุบัน และ legend',
        },
      ],
    },
    {
      id: 'run',
      heading: '5. การส่งอนุมัติการรับรู้รายเดือน',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม รับรู้รายเดือน (หรือ ประมวลผลงวด ในแท็บประวัติ) เปิดโมดัล “ส่งอนุมัติการรับรู้ — งวด …” เลือกงวดบัญชีที่ต้องการ (ต้องเป็นงวดที่ยังเปิดอยู่) ระบบแสดงพรีวิวใบสำคัญ (JV) ที่จะลงบัญชี: Dr ค่าใช้จ่าย / Cr ค่าใช้จ่ายค้างจ่าย (20170001) พร้อมยอดรวมของงวด',
        },
        {
          kind: 'callout',
          text: 'สำคัญ: กดปุ่ม ส่งอนุมัติการรับรู้ เป็นการ “สร้างงวดสถานะรออนุมัติ (PENDING)” เท่านั้น — JV ยังไม่ถูกโพสต์ทันที จะถูกโพสต์ก็ต่อเมื่องวดนั้นผ่านการอนุมัติขั้นสุดท้ายแล้ว (ดูข้อ 6) ถ้าไม่มีงวดที่เปิดอยู่ ระบบจะให้ไปเปิดงวดบัญชีก่อน',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/opcontract/04-run-modal.png',
          caption:
            'ส่งอนุมัติการรับรู้ — งวดรายเดือน: พรีวิว JV (Dr ค่าใช้จ่าย / Cr ค่าใช้จ่ายค้างจ่าย 20170001) และยอดรวม',
        },
      ],
    },
    {
      id: 'run-history',
      heading: '6. ประวัติการรับรู้รายเดือน และสายอนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ประวัติการรับรู้รายเดือน มี 2 ตาราง: (1) งวดที่รออนุมัติ/ผ่านอนุมัติ — คอลัมน์ งวด · จำนวนสัญญา · มูลค่ารวม · ผู้ส่งอนุมัติ · ผู้อนุมัติ · สถานะ ปุ่ม เปิดสายอนุมัติ (เมื่อสถานะรออนุมัติ/ตีกลับ) หรือ สายอนุมัติ และ (2) ประวัติงวดที่ลงบัญชีแล้ว (Posted) — มีปุ่ม กลับรายการ ต่อแถว',
        },
        {
          kind: 'para',
          text: 'การกลับรายการ: กดปุ่ม กลับรายการ เปิดยืนยัน “กลับรายการรับรู้ — งวด …” ระบบจะลงใบสำคัญกลับทาง Dr ค่าใช้จ่ายค้างจ่าย (20170001) / Cr ค่าใช้จ่าย (ทำซ้ำได้แบบ idempotent) แล้วกด ยืนยันกลับรายการ',
        },
        {
          kind: 'image',
          src: '/help/opcontract/05-run-history.png',
          caption:
            'ประวัติการรับรู้รายเดือน — ตารางงวดที่รออนุมัติ (เปิดสายอนุมัติ) + ตารางประวัติที่ Posted + ปุ่มกลับรายการ',
        },
        {
          kind: 'para',
          text: 'เมื่อกด เปิดสายอนุมัติ จะเปิดแผง “สายอนุมัติการรับรู้รายเดือน — งวด …” แสดง timeline สายอนุมัติ 2 ขั้น (ผู้สอบทาน → ผู้อนุมัติ) ผู้มีสิทธิ์กด ดำเนินการอนุมัติ เพื่อพิจารณาแต่ละขั้น เมื่อ ผู้อนุมัติขั้นสุดท้ายอนุมัติ ระบบจึงจะโพสต์ JV ของทุกสัญญาในงวดและเปลี่ยนรายการเป็น Posted',
        },
        {
          kind: 'image',
          src: '/help/opcontract/06-run-approval.png',
          caption:
            'สายอนุมัติการรับรู้รายเดือน — timeline 2 ขั้น (ผู้สอบทาน/ผู้อนุมัติ) และปุ่มดำเนินการอนุมัติ',
        },
      ],
    },
    {
      id: 'detail',
      heading: '7. รายละเอียดสัญญา และอัตราค่าบริการ (Escalation)',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ดู ที่แถวสัญญาในทะเบียน เปิดโมดัล “รายละเอียดสัญญา — …” แสดงข้อมูลหลัก (สาขา · บัญชีค่าใช้จ่าย · สถานะ · เริ่ม→สิ้นสุด · ค่าใช้จ่าย/เดือน · รับรู้แล้ว) แถบ มูลค่าสัญญา · Drawdown (รับรู้แล้ว %) และ ตารางรายการรับรู้รายเดือน (สามารถกลับรายการงวดที่ Posted ได้จากที่นี่)',
        },
        {
          kind: 'para',
          text: 'ด้านล่างมี ตารางอัตราค่าบริการ (Escalation) สำหรับบันทึกการขึ้นค่าบริการแบบมีผลตามงวด: เริ่มมีผล (งวด) · อัตรา/เดือน · หมายเหตุ เพิ่มหรือลบแถวอัตราได้ เพื่อให้การรับรู้งวดถัดไปใช้อัตราใหม่ตามจริง',
        },
        {
          kind: 'image',
          src: '/help/opcontract/07-detail.png',
          caption:
            'รายละเอียดสัญญา — แถบ Drawdown (รับรู้แล้ว %), ตารางรับรู้รายเดือน และตารางอัตราค่าบริการ (Escalation)',
        },
      ],
    },
    {
      id: 'renewal',
      heading: '8. สัญญาใกล้หมดอายุ / Renewal',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สัญญาใกล้หมดอายุ / Renewal แสดง KPI 3 ใบ: หมดแล้ว · ยังไม่ต่อ / 30 วันข้างหน้า / 31-90 วันข้างหน้า พร้อมตารางสัญญาที่ใกล้หมด — คอลัมน์ สัญญา · Vendor · ประเภท · วันสิ้นสุด · แจ้งยกเลิกภายใน · เหลือ (วัน) · ค่าใช้จ่าย/เดือน · การต่อสัญญา (ต่อออโต้/ต้องต่อเอง) · สถานะ',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม ต่อสัญญา เปิดโมดัล “ต่อสัญญา — …” เปรียบเทียบ สัญญาเดิม vs ใหม่ โดยตั้งค่าเริ่มต้น ค่าใช้จ่าย/เดือน เพิ่มขึ้น +3% (×1.03) ปรับ ระยะเวลา/วันเริ่ม-สิ้นสุด/แจ้งล่วงหน้า ได้ พร้อมตัวเลือก Copy เงื่อนไขจากสัญญาเดิม และ Generate Schedule อัตโนมัติ แล้วกด ต่อสัญญา & สร้าง Schedule',
        },
        {
          kind: 'image',
          src: '/help/opcontract/08-renewal.png',
          caption: 'สัญญาใกล้หมดอายุ / Renewal — KPI 3 ใบ, ตารางสัญญาใกล้หมด และปุ่มต่อสัญญา',
        },
      ],
    },
    {
      id: 'disclosure',
      heading: '9. เปิดเผย TFRS 16',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ เปิดเผย TFRS 16 จัดทำหมายเหตุประกอบงบตามข้อยกเว้น TFRS 16 ให้พร้อม: การ์ด ค่าใช้จ่ายสัญญาสั้น (Short-term lease expense) [para 53(c)] และ สินทรัพย์มูลค่าต่ำ (Low-value asset lease expense) [para 53(d)] พร้อมตารางแยกตามเหตุยกเว้น (Exemption basis) — คอลัมน์ เหตุยกเว้น · จำนวนสัญญา · ค่าใช้จ่ายที่รับรู้ (Posted)',
        },
        {
          kind: 'para',
          text: 'ยอดในหน้านี้คำนวณจากงวดที่ลงบัญชีแล้ว (Posted) ของปีที่เลือก จึงควรประมวลผล/อนุมัติงวดรายเดือนให้ครบและตั้งเหตุยกเว้น (S/L/T) ของแต่ละสัญญาให้ถูกต้อง ตัวเลขจึงจะครบถ้วน',
        },
        {
          kind: 'image',
          src: '/help/opcontract/09-disclosure.png',
          caption:
            'เปิดเผย TFRS 16 — การ์ด para 53(c)/(d) และตารางแยกตามเหตุยกเว้น (para 53 + non-lease/unclassified)',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะ',
      blocks: [
        { kind: 'para', text: 'สถานะสัญญา (แสดงในทะเบียน):' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'สัญญาที่กำลังมีผลและทยอยรับรู้ค่าใช้จ่าย'],
            ['ใกล้หมด', 'จะหมดอายุภายใน 90 วัน (ควรพิจารณาต่อสัญญา)'],
            ['หมดอายุ', 'สิ้นสุดอายุสัญญาแล้ว'],
            ['รอเริ่ม', 'ลงทะเบียนแล้วแต่ยังไม่ถึงวันเริ่มสัญญา'],
            ['⚠ ทบทวน TFRS16', 'ตั้งเหตุยกเว้นเป็นสัญญาสั้นแต่อายุเกิน 12 เดือน — ต้องตรวจสอบ'],
          ],
        },
        { kind: 'para', text: 'สถานะการอนุมัติ / การรับรู้:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รออนุมัติ', 'งวด/สัญญาที่ส่งเข้าสายอนุมัติแล้วแต่ยังไม่อนุมัติ (JV ยังไม่ลง)'],
            ['ตีกลับแก้ไข', 'ผู้สอบทาน/ผู้อนุมัติตีกลับให้แก้ไขแล้วส่งใหม่'],
            ['อนุมัติแล้ว', 'ผ่านสายอนุมัติครบ — JV ถูกโพสต์'],
            ['Posted', 'งวดรับรู้ที่ลงบัญชีแล้ว'],
            ['กลับรายการ', 'งวดที่ถูกกลับรายการ (Dr 20170001 / Cr ค่าใช้จ่าย)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมสัญญานี้ไม่ต้องตั้ง ROU เหมือนสัญญาเช่า?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เพราะเข้าข้อยกเว้น TFRS 16 (Service / Low-value / Short-term) จึงลงเป็นค่าใช้จ่ายตรงๆ ทุกเดือน ไม่ต้องตั้งสินทรัพย์สิทธิการใช้และหนี้สินตามสัญญาเช่า',
            },
          ],
        },
        { kind: 'para', text: 'กด ส่งอนุมัติการรับรู้ แล้ว JV ลงบัญชีเลยไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ยังไม่ลง — เป็นการสร้างงวดสถานะรออนุมัติเท่านั้น JV จะโพสต์เมื่องวดผ่านการอนุมัติขั้นสุดท้าย (ผู้สอบทาน → ผู้อนุมัติ)',
            },
          ],
        },
        { kind: 'para', text: 'ส่งอนุมัติงวดไม่ได้ / ไม่มีงวดให้เลือก?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ต้องมีงวดบัญชีที่ยังเปิดอยู่ก่อน ถ้าไม่มีระบบจะแจ้งให้ไปเปิดงวดบัญชีก่อน' },
          ],
        },
        { kind: 'para', text: 'ลงรับรู้ผิดงวด แก้อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ใช้ปุ่ม กลับรายการ ที่งวด Posted (Dr 20170001 / Cr ค่าใช้จ่าย) แล้วจึงประมวลผลงวดใหม่ให้ถูกต้อง',
            },
          ],
        },
        { kind: 'para', text: 'จะยกเลิกสัญญากลางทางได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'การยกเลิก (TERMINATE) ทำผ่านสายอนุมัติของ engine ไม่ใช่ปุ่มยกเลิกในแท็บ Renewal (ปุ่มดังกล่าวยังไม่เปิดใช้งาน)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Operating Service Contract — ',
              text: 'สัญญาบริการรายเดือนที่ไม่เข้า TFRS 16 ลงเป็นค่าใช้จ่ายตรงๆ',
            },
            {
              lead: 'ข้อยกเว้น TFRS 16 (S/L/T) — ',
              text: 'Service Contract (สัญญาบริการ) / Low-value Asset (สินทรัพย์มูลค่าต่ำ < 5,000 USD) / Short-term (สัญญาสั้น ≤ 12 เดือน)',
            },
            {
              lead: 'Recognition Schedule — ',
              text: 'ตารางทยอยรับรู้ค่าใช้จ่ายของสัญญาเป็นรายงวด/รายเดือน',
            },
            {
              lead: 'Drawdown — ',
              text: 'สัดส่วนที่รับรู้ค่าใช้จ่ายไปแล้วเทียบกับมูลค่าสัญญารวม (รับรู้แล้ว %)',
            },
            {
              lead: 'Escalation — ',
              text: 'การขึ้นอัตราค่าบริการแบบมีผลตามงวด (ตารางอัตราค่าบริการ)',
            },
            {
              lead: 'maker-checker — ',
              text: 'สายอนุมัติแยกหน้าที่ ผู้ส่ง → ผู้สอบทาน → ผู้อนุมัติ ก่อนลงบัญชี',
            },
            {
              lead: 'para 53(c)/(d) — ',
              text: 'ข้อกำหนดเปิดเผยตาม TFRS 16 สำหรับค่าใช้จ่ายสัญญาสั้น (53c) และสินทรัพย์มูลค่าต่ำ (53d)',
            },
          ],
        },
      ],
    },
  ],
};
